/**
 * CalendarGrid — 7-column calendar grid for admin pages.
 * Shows park icon + name + visit count per cell.
 * Spec: Section 11.7
 */
import React from 'react';
import { getParkIcon, getParkAbbr } from '../../config/parkIcons';
import { parseLocalDate, formatLocalDate } from '../../utils/dateUtils';
import { Coffee, Sunrise, Sun, Moon } from 'lucide-react';
// Trip import — reserved for future use

interface DayInfo {
  date: string;
  parks: string[];
  visitCount?: number;
  visitPeriod?: 'MORNING' | 'MIDDAY' | 'EVENING';
}

interface CalendarGridProps {
  days: DayInfo[];
  startDate: string;
  endDate: string;
}

const PERIOD_MAP: Record<string, { label: string; Icon: React.ComponentType<{ className?: string }>; color: string }> = {
  MORNING:  { label: 'Morning',  Icon: Sunrise, color: 'text-amber-500 dark:text-amber-300' },
  MIDDAY:   { label: 'Midday',   Icon: Sun,     color: 'text-orange-500 dark:text-orange-300' },
  EVENING:  { label: 'Evening',  Icon: Moon,    color: 'text-indigo-500 dark:text-indigo-300' },
};

function PeriodPill({ period }: { period: 'MORNING' | 'MIDDAY' | 'EVENING' }) {
  const { label, Icon, color } = PERIOD_MAP[period];
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </span>
  );
}

export function CalendarGrid({ days, startDate, endDate }: CalendarGridProps): React.ReactElement {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  // Build a map of day → info
  const infoMap = new Map<string, DayInfo>();
  days.forEach((d) => infoMap.set(d.date, d));

  // Fill gaps with rest days
  const allDays: DayInfo[] = [];
  const current = new Date(start);
  while (current <= end) {
    const dateStr = formatLocalDate(current);
    const info = infoMap.get(dateStr);
    if (info) {
      allDays.push(info);
    } else {
      allDays.push({ date: dateStr, parks: ['rest'], visitCount: 0 });
    }
    current.setDate(current.getDate() + 1);
  }

  // Group into weeks starting Monday
  const weeks: DayInfo[][] = [];
  let week: DayInfo[] = [];
  const firstDay = parseLocalDate(allDays[0]?.date);
  const dayOfWeek = (firstDay?.getDay() || 0) === 0 ? 6 : (firstDay?.getDay() || 0) - 1;
  if (dayOfWeek > 0 && allDays.length > 0) {
    // Add padding for days before the first day
    for (let i = 0; i < dayOfWeek; i++) {
      const padDate = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate() - (dayOfWeek - i));
      week.push({
        date: formatLocalDate(padDate),
        parks: ['padding'], visitCount: 0,
      });
    }
  }
  allDays.forEach((d) => {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  });
  if (week.length > 0) weeks.push(week);

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 dark:text-gray-300 mb-1 w-full">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="py-2">{d}</div>
        ))}
      </div>
      {weeks.map((wk, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1 w-full">
          {wk.map((d) => {
            const isToday = d.date === formatLocalDate(new Date());
            const isRest = d.parks[0] === 'rest';
            const isPadding = d.parks[0] === 'padding';
            const ParkIcon = (!isRest && !isPadding) ? getParkIcon(d.parks[0]) : null;
            const abbr = getParkAbbr(d.parks[0]);
            const dDate = parseLocalDate(d.date);
            const dayNum = dDate.getDate();
            const monthAbbr = dDate.toLocaleDateString('en-US', { month: 'short' });

            const dateLabel = isPadding
              ? <span className="text-[10px] font-medium text-gray-300 dark:text-gray-600">{dayNum}</span>
              : <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">{monthAbbr} {dayNum}</span>;

            return (
              <div key={d.date}
                className={`rounded-lg p-3 text-center min-h-[88px] flex flex-col items-center justify-center gap-1
                  ${isPadding ? 'bg-transparent' : isRest ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-surface-dark-card shadow-sm'}
                  ${isToday && !isPadding ? 'ring-2 ring-disney-blue-500' : ''}`}>
                {dateLabel}
                {ParkIcon ? (
                  <>
                    <ParkIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 leading-tight">{abbr}</span>
                    {d.visitCount != null && d.visitCount > 0 && (
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">{d.visitCount} rides</span>
                    )}
                    {d.visitPeriod && <PeriodPill period={d.visitPeriod} />}
                  </>
                ) : isRest ? (
                  <>
                    <Coffee className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 leading-tight">rest</span>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
